// =====================================================
// UNIMATCHPLAY 2026 — TypeScript Types
// =====================================================

// ===== Database entities =====

export interface Tournament {
  id: string;
  name: string;
  year: number;
  date: string;
  venue: string;
  created_at: string;
  updated_at: string;
}

export interface Team {
  id: string;
  tournament_id: string;
  name: string;
  short_name?: string;
  color: string;
  logo_url?: string;
  group_name: 'A' | 'B' | null;
  seed: number | null;
  created_at: string;
  updated_at: string;
}

export interface Player {
  id: string;
  team_id: string | null;
  tournament_id: string;
  name: string;
  handicap: number | null;
  gender: 'M' | 'F' | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  is_admin: boolean;
  auth_id: string | null;
  created_at: string;
  updated_at: string;
}

export type MatchUpRound =
  | 'semi_1' | 'semi_2' | 'semi_3' | 'semi_4'
  | 'place_7' | 'place_5' | 'place_3' | 'final';

export type MatchUpStatus = 'scheduled' | 'in_progress' | 'completed';

export interface MatchUp {
  id: string;
  tournament_id: string;
  round: MatchUpRound;
  label: string;
  team_a_id: string | null;
  team_b_id: string | null;
  scheduled_time: string | null;
  sort_order: number;
  status: MatchUpStatus;
  locked: boolean;
  source_match_winner: string | null;
  source_match_loser: string | null;
  created_at: string;
  updated_at: string;
  // Joined data (optional)
  team_a?: Team;
  team_b?: Team;
  games?: Game[];
}

export type GameType = 'single' | 'foursome';
export type GameStatus = 'not_started' | 'in_progress' | 'completed';
export type WinnerSide = 'a' | 'b' | 'halved';
export type TeamSide = 'a' | 'b';

export interface Game {
  id: string;
  match_up_id: string;
  type: GameType;
  game_number: number; // 1, 2, 3
  // Singles
  player_a_id: string | null;
  player_b_id: string | null;
  // Foursome
  foursome_a1_id: string | null;
  foursome_a2_id: string | null;
  foursome_b1_id: string | null;
  foursome_b2_id: string | null;
  // Result
  status: GameStatus;
  winner_side: WinnerSide | null;
  result_text: string | null;
  holes_played: number;
  tee_time: string | null;  // e.g. "08:00" – individual game start time

  created_at: string;
  updated_at: string;
  // Joined data (optional)
  player_a?: Player;
  player_b?: Player;
  foursome_a1?: Player;
  foursome_a2?: Player;
  foursome_b1?: Player;
  foursome_b2?: Player;
  hole_scores?: HoleScore[];
}

export interface HoleScore {
  id: string;
  game_id: string;
  hole_number: number;
  team_side: TeamSide;
  strokes: number;
  created_at: string;
  updated_at: string;
}

// ===== Scoring / Computed types =====

export interface MatchPlayState {
  holesPlayed: number;
  holesRemaining: number;
  /** Which side leads: 'a', 'b', or null if tied */
  leaderSide: TeamSide | null;
  /** How many holes the leader is up by */
  lead: number;
  /** Is the match decided (early finish or all holes played) */
  isFinished: boolean;
  /** Is the match in dormie (lead equals remaining holes) */
  isDormie: boolean;
  /** Winner side, null if not finished */
  winnerSide: WinnerSide | null;
  /** Text result: "3&2", "1 UP", "A/S", null if in progress */
  resultText: string | null;
}

export interface GameResult {
  gameId: string;
  type: GameType;
  matchPlayState: MatchPlayState;
  winnerSide: WinnerSide | null;
  points: { a: number; b: number };
}

export interface MatchUpResult {
  matchUpId: string;
  teamAPoints: number;
  teamBPoints: number;
  /** Live points include in-progress games (leader gets provisional point) */
  liveTeamAPoints: number;
  liveTeamBPoints: number;
  games: GameResult[];
  winnerTeamId: string | null;
  isCompleted: boolean;
}

export interface TeamStanding {
  position: number;
  teamId: string;
  teamName: string;
  teamColor: string;
  finalRound: string;
  matchUpResult: string;
}

// ===== Player Statistics =====

export interface PlayerGameStats {
  gameId: string;
  gameType: GameType;
  opponentName: string;
  partnerName?: string; // foursome partner
  matchResult: string;
  won: boolean | null; // true=win, false=loss, null=halved
  holesPlayed: number;
  birdies: number;
  eagles: number;
  holesWon: number;
  holesHalved: number;
  holesLost: number;
}

export interface PlayerStats {
  playerId: string;
  playerName: string;
  teamId: string;
  teamName: string;
  teamColor: string;
  totalHolesPlayed: number;
  birdies: number;
  eagles: number;
  holesWon: number;
  holesHalved: number;
  holesLost: number;
  winRate: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesHalved: number;
  gamesLost: number;
  perGameStats: PlayerGameStats[];
}

// ===== Course =====

export interface CourseHole {
  number: number;
  par: number;
  si: number; // stroke index (informational only)
}

export interface Course {
  name: string;
  holes: CourseHole[];
  totalPar: number;
}

// ===== UI State =====

export type UserRole = 'admin' | 'player' | 'public';

export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  player?: Player;
}

// ===== Bracket Propagation =====

export interface BracketPropagation {
  matchUpId: string;
  teamAId: string;
  teamBId: string;
}

/** Bracket mapping: which semifinal feeds which final match */
export const BRACKET_MAP: Record<string, { winnerGoesTo: string; loserGoesTo: string }> = {
  'semi_1': { winnerGoesTo: 'place_5', loserGoesTo: 'place_7' },
  'semi_2': { winnerGoesTo: 'place_5', loserGoesTo: 'place_7' },
  'semi_3': { winnerGoesTo: 'final', loserGoesTo: 'place_3' },
  'semi_4': { winnerGoesTo: 'final', loserGoesTo: 'place_3' },
};
